import { ChildMutateProps } from '@apollo/client/react/hoc';
// TODO update this to import from commmons
// import { gql } from '@reverbdotcom/commons/src/gql';
// eslint-disable-next-line no-restricted-imports
import { gql } from '@apollo/client';
import React from 'react';
import I18n from 'i18n-js';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import {
  RemoveFeedEntry,
  reverb_feed_FeedEntry,
  Listing,
  Article,
  core_apimessages_Listing_ListingType as ListingType,
} from '@reverbdotcom/commons/src/gql/graphql';

import { ITEM_WITH_SLUG_ROUTE } from '../components/shared/url_helpers';

import { DisplayStyle, WatchBadge } from '@reverbdotcom/commons/src/components/watch_badge';
import { WatchParentComponentNames } from '@reverbdotcom/commons/src/event_tracking/watchlist';
import { conditionDisplayText } from '@reverbdotcom/commons/src/components/condition_display';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';

import { buildListingClickEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { WEB } from '@reverbdotcom/commons/src/constants';
import { ImageCarousel } from '@reverbdotcom/commons';
import { RCListingCard } from '@reverbdotcom/cadence/components';
import { FeedOIcon } from '@reverbdotcom/cadence/icons/react';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { buildListingForBuyer, getA11yPriceDescription, getEyebrow } from '@reverbdotcom/commons/src/listing_card_helpers';

export interface IExternalProps {
  entry?: reverb_feed_FeedEntry;
  position?: number;
  hideRemoveButton?: boolean;
  showFlags?: boolean;
}

type IProps = ChildMutateProps<IExternalProps, RemoveFeedEntry.Mutation, RemoveFeedEntry.Variables>;

export interface IEntryWithImages {
  images: {
    source: string;
  }[];
}

const LISTING_TYPE = 'LISTING';
const ARTICLE_TYPE = 'ARTICLE';

function getLC(locale, lcs) {
  const code = locale.split('-')[0];
  return lcs.filter(lc => lc.locale === code)[0];
}

function shouldTrackInteraction(entry: reverb_feed_FeedEntry): boolean {
  return !!(entry && entry.type === LISTING_TYPE && entry.entry);
}

/**
 * This is just the start for RQL and the Feed. We will need to backport
 * support for the rest of the types in the
 * discovery-ui/src/components/discovery/feed/feed_item.tsx component.
 *
 * In doing so we will need to add additional type unions both in RQL
 * and in the ApolloClient
 */

export function RQLFeedItem({ entry, position = 0, mutate, hideRemoveButton, showFlags }: IProps) {
  const isEmpty = !entry;
  const user = useUser();
  const localizedContents = entry?.localizedContents ?? [];
  const content = getLC(I18n.locale, localizedContents);

  function tryGetEntry<K>() {
    return entry?.entry as K;
  }

  const listing = isType(LISTING_TYPE) ? tryGetEntry<Listing>() : null;
  const article = isType(ARTICLE_TYPE) ? tryGetEntry<Article>() : null;
  const entryIsLiveListing = entry?.type === 'LISTING' && !isSold();

  function isType(type) {
    return entry?.type === type;
  }

  function isSold() {
    if (!isType(LISTING_TYPE)) { return false; }

    return listing.state !== 'live';
  }

  function removeFeedItem() {
    mutate({
      variables: {
        input: {
          entryId: String(entry.id),
          entryType: entry.type,
        },
      },
      optimisticResponse: {
        __typename: 'Mutation',
        removeFeedEntry: {
          __typename: 'reverb_feed_RemoveEntryResponse',
          entry: {
            __typename: 'reverb_feed_FeedEntry',
            id: entry.id,
            deleted: true,
            type: entry.type,
          },
        },
      },
    });
  }

  function getImages() {
    if (!entry) { return null; }

    if (isType(LISTING_TYPE) || isType(ARTICLE_TYPE)) {
      const images = ((entry?.entry as IEntryWithImages)?.images || []).map(i => i.source);
      return <ImageCarousel images={images} />;
    }

    return null;
  }

  function getHref() {
    if (isEmpty) { return '#'; }

    if (isType(LISTING_TYPE)) {
      return ITEM_WITH_SLUG_ROUTE.expand({
        slug: listing.slug,
        id: listing.id,
      });
    }

    if (isType(ARTICLE_TYPE)) {
      return article.webLink.href;
    }

    return '#';
  }

  function clickEvent() {
    if (!shouldTrackInteraction(entry)) {
      return null;
    }

    const entryInstance = entry?.entry;
    if (entryInstance?.__typename !== 'Listing') {
      return null;
    }

    return buildListingClickEvent({
      listing: entryInstance,
      position,
      displayStyle: null,
    });
  }

  function openLinkInNewTab() {
    return user?.deviceName === WEB;
  }

  function getConditionLabelForRCCard() {
    if (isType(ARTICLE_TYPE)) {
      return article?.articleCategories[0]?.name;
    }

    if (listing?.listingType === ListingType.DIGITAL || !listing?.condition) {
      return null;
    }

    return conditionDisplayText(listing?.condition?.conditionSlug, listing?.condition?.displayName);
  }

  if (isEmpty) {
    return <RCListingCard.Skeleton />;
  }

  if (entry?.deleted) {
    return null;
  }

  const decoratedListing = buildListingForBuyer(listing, user);

  const price = isType(LISTING_TYPE) ? ({
    priceDisplay: decoratedListing.primaryPriceDisplay,
    discountPriceDescription: decoratedListing.discountPriceDescription,
    discountPriceDisplay: decoratedListing.discountedPriceDisplay,
    a11yPriceDescription: getA11yPriceDescription(decoratedListing),
    originalPriceString: decoratedListing.originalPriceString,
  }) : null;

  const nudges = content?.shortDescription ? [{ icon: FeedOIcon, label: content?.shortDescription }] : [];

  if (showFlags) {
    nudges.push({
      label: listing.shop.address.country.name,
      icon: () => (
        <span className="listing-shop-country-nudge">
          <span className={`flag-icon flag-icon-${listing.shop.address.country.countryCode.toLowerCase()}`} />
        </span>
      ),
    });
  }

  const eyebrow = getEyebrow(listing, false);

  return (
    <RCListingCard
      linkElement={(children) => (
        <CoreLink
          to={getHref()}
          target={openLinkInNewTab() ? '_blank' : undefined}
          clickEvent={clickEvent()}
        >
          {children}
        </CoreLink>
      )}
      thumbnailElement={getImages()}
      title={entry?.entry?.title}
      conditionLabel={getConditionLabelForRCCard()}
      price={price}
      nudges={nudges}
      removeButton={!hideRemoveButton && {
        onClick: removeFeedItem,
        label: I18n.t('discovery.feed.removeTooltip'),
      }}
      eyebrow={eyebrow}
      fullHeight
      favoriteButtonElement={entryIsLiveListing && (
        <WatchBadge
          listing={listing}
          displayStyle={DisplayStyle.RC_FAVORITE_BUTTON}
          parentComponentName={WatchParentComponentNames.Feed}
          useToast
        />
      )}
    />
  );
}

const removeFeedEntryMutation = gql`
mutation RemoveFeedEntry($input: Input_reverb_feed_RemoveEntryRequest) {
  removeFeedEntry(input: $input) {
    entry {
      id
      type
      deleted
    }
  }
}
`;

export default withGraphql<IExternalProps, RemoveFeedEntry.Mutation, RemoveFeedEntry.Variables>(
  removeFeedEntryMutation,
)(RQLFeedItem);
